import { IntlShape } from 'react-intl'
import { MapDataset, MapType } from 'features/maps/types'
import { BuilderLayerWithData, GeometryType, KeplerTable, LayerType, LayerTypes, SpatialDataType } from '../types'
import {
  getGeometryFromTilesetDataset,
  getSchemeFromTilesetDataset,
  getgeoColumnFromTilesetDataset,
  shouldLoadAsDynamicTiling
} from '../state/builderSliceUtils'

import IconPolygonAlt from 'assets/icons/layers/polygon_alt.svg?react'
import IconLineAlt from 'assets/icons/layers/line_alt.svg?react'
import IconPointAlt from 'assets/icons/layers/point_alt.svg?react'
import IconH3 from 'assets/icons/layers/h3_alt.svg?react'
import IconGrid from 'assets/icons/layers/quadbin_alt.svg?react'
import IconHeatmap from 'assets/icons/layers/heatmap_alt.svg?react'
import IconCluster from 'assets/icons/layers/cluster_alt.svg?react'
import IconRaster from 'assets/icons/layers/raster.svg?react'

import { parseGeoColumn } from './geoColumn'
import { SchemaFieldType } from 'features/data-explorer/types'
import { spatialDataTypeFromTilingSchemeTo } from './spatialDataSettings'
import { getTilejsonGeometryType } from './tilejson'

export const TILESET_SWITCH_SUPPORTED_LAYER_TYPES: LayerType[] = [
  LayerTypes.Quadbin,
  LayerTypes.H3,
  LayerTypes.Tileset,
  LayerTypes.HeatmapTile,
  LayerTypes.ClusterTile
]

export const layerChangeRequiresDatasetUpdate = (layerTypeOld: LayerType, layerTypeNew: LayerType) => {
  return TILESET_SWITCH_SUPPORTED_LAYER_TYPES.includes(layerTypeNew)
}

type LayerTypeDefinition = {
  labelKey: string
  layerType: LayerType
  icon: React.FunctionComponent
}

const layerTypeDefinitions = {
  tilesetPoint: {
    labelKey: 'tilesetPoint',
    layerType: LayerTypes.Tileset,
    icon: IconPointAlt
  },
  aggregatedTilesetPoint: {
    labelKey: 'aggregatedTilesetPoint',
    layerType: LayerTypes.Tileset,
    icon: IconPointAlt
  },
  tilesetPolygon: {
    labelKey: 'tilesetPolygon',
    layerType: LayerTypes.Tileset,
    icon: IconPolygonAlt
  },
  aggregatedTilesetPolygon: {
    labelKey: 'aggregatedTilesetPolygon',
    layerType: LayerTypes.Tileset,
    icon: IconPolygonAlt
  },
  tilesetLine: {
    labelKey: 'tilesetLine',
    layerType: LayerTypes.Tileset,
    icon: IconLineAlt
  },
  aggregatedTilesetLine: {
    labelKey: 'aggregatedTilesetLine',
    layerType: LayerTypes.Tileset,
    icon: IconLineAlt
  },
  quadbin: {
    layerType: LayerTypes.Quadbin,
    labelKey: 'quadbin',
    icon: IconGrid
  },
  quadbinGeom: {
    layerType: LayerTypes.Quadbin,
    labelKey: 'quadbinGeom',
    icon: IconGrid
  },
  h3: {
    layerType: LayerTypes.H3,
    labelKey: 'h3',
    icon: IconH3
  },
  h3Geom: {
    layerType: LayerTypes.H3,
    labelKey: 'h3Geom',
    icon: IconH3
  },
  heatmap: {
    layerType: LayerTypes.HeatmapTile,
    labelKey: 'heatmap',
    icon: IconHeatmap
  },
  cluster: {
    layerType: LayerTypes.ClusterTile,
    labelKey: 'cluster',
    icon: IconCluster
  },
  raster: {
    layerType: LayerTypes.Raster,
    labelKey: 'raster',
    icon: IconRaster
  },
  rasterNoOverviews: {
    layerType: LayerTypes.Raster,
    labelKey: 'rasterNoOverviews',
    icon: IconRaster
  }
}

export function getSupportedLayerTypes({
  keplerDataset,
  builderDataset,
  intl,
  isH3PointAggregationEnabled
}: {
  keplerDataset: KeplerTable
  builderDataset: MapDataset
  intl: IntlShape
  isH3PointAggregationEnabled: boolean
}) {
  const getDefinitions = (): (LayerTypeDefinition | false)[] => {
    if (builderDataset.type === MapType.raster) {
      const hasOverviews =
        keplerDataset?.metadata?.raster_metadata?.minresolution !==
        keplerDataset?.metadata?.raster_metadata?.maxresolution
      return [hasOverviews ? layerTypeDefinitions.raster : layerTypeDefinitions.rasterNoOverviews]
    }

    const geoColumn = getgeoColumnFromTilesetDataset(keplerDataset)
    const schema = keplerDataset?.metadata?.schema

    const parsedGeocolumn = parseGeoColumn(geoColumn)
    const spatialDataColumn = parsedGeocolumn?.spatialDataColumn
    const field = schema?.find((f) => f.name === spatialDataColumn)
    const dynamicTiling = shouldLoadAsDynamicTiling(builderDataset)
    const isAggregated = !!builderDataset?.aggregationExp

    const spatialDataType =
      parsedGeocolumn?.spatialDataType ||
      (builderDataset.type === MapType.tileset
        ? spatialDataTypeFromTilingSchemeTo(getSchemeFromTilesetDataset(keplerDataset))
        : undefined) ||
      SpatialDataType.Geo

    const spatialDataColumnIsGeometry = field?.type === SchemaFieldType.Geometry

    switch (spatialDataType) {
      case SpatialDataType.H3:
        if (!isH3PointAggregationEnabled || !dynamicTiling || !spatialDataColumnIsGeometry) {
          return [layerTypeDefinitions.h3]
        }
        return [
          layerTypeDefinitions.tilesetPoint,
          layerTypeDefinitions.quadbinGeom,
          layerTypeDefinitions.h3Geom,
          layerTypeDefinitions.heatmap,
          layerTypeDefinitions.cluster
        ]
      case SpatialDataType.Quadbin: {
        if (!dynamicTiling || !spatialDataColumnIsGeometry) {
          return [layerTypeDefinitions.quadbin]
        }
        return [
          layerTypeDefinitions.tilesetPoint,
          layerTypeDefinitions.quadbinGeom,
          isH3PointAggregationEnabled && layerTypeDefinitions.h3Geom,
          layerTypeDefinitions.heatmap,
          layerTypeDefinitions.cluster
        ]
      }
      case SpatialDataType.Geo: {
        const geometry = getGeometryFromTilesetDataset(keplerDataset)
        switch (geometry) {
          case GeometryType.Point:
          case GeometryType.MultiPoint:
            return [
              isAggregated ? layerTypeDefinitions.aggregatedTilesetPoint : layerTypeDefinitions.tilesetPoint,
              dynamicTiling && layerTypeDefinitions.quadbinGeom,
              dynamicTiling && isH3PointAggregationEnabled && layerTypeDefinitions.h3Geom,
              dynamicTiling && layerTypeDefinitions.heatmap,
              dynamicTiling && layerTypeDefinitions.cluster
            ]
          case GeometryType.Line:
          case GeometryType.LineString:
          case GeometryType.MultiLineString:
            return [isAggregated ? layerTypeDefinitions.aggregatedTilesetLine : layerTypeDefinitions.tilesetLine]
          case GeometryType.Polygon:
          case GeometryType.MultiPolygon:
            return [isAggregated ? layerTypeDefinitions.aggregatedTilesetPolygon : layerTypeDefinitions.tilesetPolygon]
          default:
            console.warn(`getSupportedLayerTypes: Unknown geometry type: ${geometry} for dataset ${keplerDataset.id}`)
        }
      }
    }
    return []
  }

  return getDefinitions()
    .filter((v): v is LayerTypeDefinition => Boolean(v))
    .map((definition: LayerTypeDefinition) => ({
      ...definition,
      label: intl.formatMessage({ id: `builder.layerTypes.${definition.labelKey}.label` }),
      title: intl.formatMessage({ id: `builder.layerTypes.${definition.labelKey}.title` })
    }))
}

export enum LayerFeatureType {
  POINT,
  LINE,
  POLYGON,
  GRID,
  HEXAGON,
  CLUSTER,
  HEATMAP,
  H3,
  RASTER,
  UNKNOWN
}

const layerTypeToFeatureTypeMap = {
  [LayerTypes.Point]: LayerFeatureType.POINT,
  [LayerTypes.Line]: LayerFeatureType.LINE,
  [LayerTypes.HeatmapTile]: LayerFeatureType.HEATMAP,
  [LayerTypes.ClusterTile]: LayerFeatureType.CLUSTER,
  polygon: LayerFeatureType.POLYGON,
  grid: LayerFeatureType.GRID,
  hexagon: LayerFeatureType.HEXAGON,
  hexagonId: LayerFeatureType.H3,
  cluster: LayerFeatureType.CLUSTER,
  heatmap: LayerFeatureType.HEATMAP,
  [LayerTypes.Raster]: LayerFeatureType.RASTER
}

export const getLayerFeatureType = ({ type: layerType, data }: BuilderLayerWithData): LayerFeatureType => {
  const directMapping = layerTypeToFeatureTypeMap[layerType]

  if (directMapping >= 0) {
    return directMapping
  }

  const geometryType = data ? getTilejsonGeometryType(data) : undefined
  if (layerType === LayerTypes.GeoJSON || layerType === LayerTypes.Tileset) {
    if (geometryType === 'Point' || geometryType === 'MultiPoint') {
      return LayerFeatureType.POINT
    } else if (geometryType === 'LineString' || geometryType === 'MultiLineString' || geometryType === 'Line') {
      return LayerFeatureType.LINE
    } else {
      return LayerFeatureType.POLYGON
    }
  }

  return LayerFeatureType.UNKNOWN
}

/**
 * Maps geometry type strings (as found in {@link getGeometryFromTilesetDataset})
 * to named layer types we can display in the UI, e.g. for the layer list and layer
 * type selection.
 */
export const GEOMETRY_TO_LAYER_TYPE = {
  Point: LayerTypes.Point,
  MultiPoint: LayerTypes.Point,
  Line: LayerTypes.Line,
  LineString: LayerTypes.Line,
  MultiLineString: LayerTypes.Line,
  Polygon: LayerTypes.GeoJSON,
  MultiPolygon: LayerTypes.GeoJSON
} as const
